import setup from '../assets/data/setup.json';

const LANGUAGE_SKILLS = new Set([
  'JavaScript', 'TypeScript', 'PHP', 'Java', 'Python', 'HTML', 'CSS',
]);

const FRAMEWORK_SKILLS = new Set([
  'React', 'Next.js', 'Express', 'Tailwind', 'Mantine', 'WordPress',
]);

const TESTING_SKILLS = new Set(['Playwright']);

const SECURITY_SKILLS = new Set(['Authentication']);

function unique(items: string[]): string[] {
  return Array.from(new Set(items));
}

function collectAllSkills(): string[] {
  return unique(Object.values(setup.skills).flat());
}

function pickSkills(predicate: (skill: string) => boolean): string[] {
  return collectAllSkills().filter(predicate);
}

export function categorizeTechnicalExpertise(
  priorityKeywords: string[] = []
): Record<string, string[]> {
  const priority = (skills: string[]) => {
    if (!priorityKeywords.length) return skills;
    return [...skills].sort((a, b) => {
      const aScore = priorityKeywords.filter((k) => a.toLowerCase().includes(k)).length;
      const bScore = priorityKeywords.filter((k) => b.toLowerCase().includes(k)).length;
      return bScore - aScore;
    });
  };

  const categories: Record<string, string[]> = {
    Languages: priority(pickSkills((s) => LANGUAGE_SKILLS.has(s))),
    Frameworks: priority(pickSkills((s) => FRAMEWORK_SKILLS.has(s))),
    Frontend: priority(setup.skills.Frontend || []),
    Backend: priority(setup.skills.Backend || []),
    Databases: priority(setup.skills.Databases || []),
    Cloud: priority(setup.skills.Cloud || []),
    DevOps: priority(
      pickSkills((s) =>
        ['CI/CD', 'Docker', 'Nginx', 'PM2', 'Linux', 'AWS', 'S3'].includes(s)
      )
    ),
    Testing: priority([
      ...pickSkills((s) => TESTING_SKILLS.has(s)),
      ...(setup.experiences.some((e) =>
        (e.technologies || []).some((t) => t.toLowerCase().includes('playwright'))
      )
        ? ['Playwright']
        : []),
    ]),
    Automation: priority(setup.skills.Automation || []),
    AI: priority(setup.skills.AI || []),
    Security: priority([
      ...pickSkills((s) => SECURITY_SKILLS.has(s)),
      'Role-Based Access Control',
      'Audit Logging',
    ]),
    Integrations: priority(setup.integrations.map((i) => i.name)),
    Mobile: priority(setup.skills.Mobile || []),
  };

  return Object.fromEntries(
    Object.entries(categories).filter(([, items]) => items.length > 0)
  );
}

export function categorizeSkillsForResume(
  priorityKeywords: string[] = []
): Record<string, string[]> {
  const expertise = categorizeTechnicalExpertise(priorityKeywords);
  const grouped: Record<string, string[]> = {
    Languages: expertise.Languages || [],
    Frontend: expertise.Frontend || [],
    Backend: expertise.Backend || [],
    Databases: expertise.Databases || [],
    Cloud: expertise.Cloud || [],
    DevOps: expertise.DevOps || [],
    Automation: expertise.Automation || [],
    AI: expertise.AI || [],
    Integrations: expertise.Integrations || [],
    Mobile: expertise.Mobile || [],
  };

  return Object.fromEntries(
    Object.entries(grouped).filter(([, items]) => items.length > 0)
  );
}

export function getPrimaryTechnologies(variantKeywords: string[]): string[] {
  const all = collectAllSkills();
  const scored = all
    .map((skill) => ({
      skill,
      score: variantKeywords.filter((k) => skill.toLowerCase().includes(k)).length,
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.skill);

  if (scored.length >= 4) return scored.slice(0, 6);
  return unique([
    ...scored,
    'React',
    'Next.js',
    'Node.js',
    'TypeScript',
    'AWS',
    'MySQL',
  ]).slice(0, 6);
}
